import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { SelectionCircle } from "./SelectionCircle";
import { useTranslation } from "react-i18next";

export type MultiSelectOption<T extends number | string = number> = {
  id: T;
  label: string;
};

export type MultiSelectFilterProps<T extends number | string = number> = {
  label: string;
  options: MultiSelectOption<T>[];
  selectedIds: Set<T>;
  onChange: (next: Set<T>) => void;
  disabled?: boolean;
  mobileAlign?: "left" | "right";
};

// Custom dropdown (not a native multi-select, which can't show a running
// selection count on the trigger or a "Clear" row) shared by the brand and
// category product filters. Mirrors AnnouncementPicker's click-outside/Escape
// handling.
export function MultiSelectFilter<T extends number | string = number>({
  label,
  options,
  selectedIds,
  onChange,
  disabled,
  mobileAlign = "left",
}: MultiSelectFilterProps<T>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const list = optionsRef.current;
    if (!list) return;

    const updateOverflowHint = () => {
      const bottomPadding = Number.parseFloat(getComputedStyle(list).paddingBottom) || 0;
      const remainingScroll = list.scrollHeight - list.scrollTop - list.clientHeight;
      setHasOverflow(list.scrollHeight - bottomPadding - list.clientHeight > 1);
      setIsAtBottom(remainingScroll <= 1);
    };

    updateOverflowHint();
    const resizeObserver = new ResizeObserver(updateOverflowHint);
    resizeObserver.observe(list);
    return () => resizeObserver.disconnect();
  }, [isOpen, options.length]);

  const toggle = (id: T) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const count = selectedIds.size;

  return (
    <div ref={containerRef} className={`relative ${isOpen ? "z-40" : "z-0"}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(prev => !prev)}
        className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
          count > 0 ? "bg-brand/10 border-brand text-brand" : "bg-app-control border-app-border text-foreground hover:bg-app-control-hover"
        }`}
      >
        <span>{label}</span>
        <span
          aria-hidden={count === 0}
          className={`inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-brand text-white text-[10px] leading-none ${count === 0 ? "invisible" : ""}`}
        >
          {count}
        </span>
        <ChevronDown size={14} className={`shrink-0 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <div
        className={`absolute z-30 top-full mt-1 w-56 max-w-[80vw] bg-app-surface text-foreground rounded-xl shadow-lg ring-1 ring-app-border overflow-hidden transition-all duration-150 ease-in-out ${
          mobileAlign === "right" ? "right-0 left-auto sm:right-auto sm:left-0" : "left-0"
        } ${
          isOpen ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-1 scale-95 pointer-events-none"
        }`}
      >
        <div className="relative">
          <ul
            ref={optionsRef}
            role="listbox"
            aria-label={label}
            aria-multiselectable="true"
            onScroll={() => {
              const list = optionsRef.current;
              if (!list) return;
              setIsAtBottom(list.scrollHeight - list.scrollTop - list.clientHeight <= 1);
            }}
            className={`filter-options-scroll max-h-64 overflow-y-scroll pt-1 pr-1 ${hasOverflow ? "pb-14" : "pb-1"}`}
          >
            {options.map(option => {
              const checked = selectedIds.has(option.id);
              return (
                <li key={option.id} role="option" aria-selected={checked}>
                  <button
                    type="button"
                    onClick={() => toggle(option.id)}
                    className={`group w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-brand/10 transition-colors duration-150 cursor-pointer ${
                      checked ? "text-brand font-medium" : "text-foreground"
                    }`}
                  >
                    <SelectionCircle checked={checked} />
                    <span className="flex-1 min-w-0 truncate">{option.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {hasOverflow && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex h-18 items-end justify-center bg-gradient-to-t from-app-surface via-app-surface/95 to-transparent pb-1.5">
              <button
                type="button"
                onClick={() => {
                  const list = optionsRef.current;
                  if (!list) return;
                  list.scrollTo({ top: isAtBottom ? 0 : list.scrollHeight, behavior: "smooth" });
                }}
                className="group pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-app-control/95 my-1 px-3 py-2 text-xs font-medium text-brand shadow-sm ring-1 ring-brand/15 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:scale-105 hover:bg-brand hover:text-white hover:shadow-md active:translate-y-0 active:scale-95 cursor-pointer"
              >
                {t(isAtBottom ? "products.scrollAllTheWayUp" : "products.scrollForMore")}
                <ChevronDown
                  size={14}
                  aria-hidden="true"
                  className={`transition-transform duration-200 ease-out ${
                    isAtBottom ? "rotate-180 group-hover:-translate-y-0.5" : "group-hover:translate-y-0.5"
                  }`}
                />
              </button>
            </div>
          )}
        </div>
        {count > 0 && (
          <button
            type="button"
            onClick={() => onChange(new Set())}
            className="w-full border-t border-app-border px-3 py-2 text-left text-sm text-app-muted hover:bg-app-surface-hover transition-colors duration-150 cursor-pointer"
          >
            {t("products.clearFilter")}
          </button>
        )}
      </div>
    </div>
  );
}
